// Detecta insignias de racha recién DESBLOQUEADAS —ganadas (`insignias`) o provisionales
// (`insigniasProvisionales`, RF-036: el récord proyectado ya las alcanza aunque el cierre semanal
// real todavía no las otorgó)— y las entrega en cola, una a la vez, para que StreakWidget dispare
// la celebración (BadgeCelebration). Vive fuera de StreakWidget para no mezclar la lógica de
// detección con el layout del widget.
//
// Por qué el conjunto de "desbloqueada" es la UNIÓN de las dos listas y no solo `insignias`: el
// widget ya pinta una insignia provisional como desbloqueada (con su propia etiqueta "AL CIERRE",
// ver StreakWidget.tsx) desde el momento en que el período en curso la alcanza. Esperar a que
// también aparezca en `insignias` — es decir, al cierre semanal real — para celebrar se sentiría
// tarde: el usuario ya la vio "abierta" en la grilla, posiblemente en una sesión anterior a que la
// celebración llegara a dispararse.
//
// "Ya vista" se guarda en localStorage por usuario, no en memoria: así funciona sin importar cuándo
// se vuelve a abrir el widget, sea que el código lo haya sumado la proyección en vivo del período en
// curso o el cierre semanal real que corrió mientras la app estaba cerrada. Una vez que un código se
// marca visto —ya sea porque llegó provisional o ganado—, no se vuelve a celebrar: el mismo código
// pasando de provisional a `insignias` en el cierre real NO dispara una segunda celebración.
//
// La primera vez que se ve a un usuario (sin registro previo en localStorage) las insignias que YA
// tenía se registran como vistas SIN celebrarlas — evita un burst retroactivo de todas las insignias
// históricas la primera vez que alguien abre la app después de que esta funcionalidad se despliega.
import { useEffect, useState } from 'react';
import type { BadgeDto, StreakDto } from '@/api/recognition';

const STORAGE_PREFIX = 'pmp.streak.badgesSeen.';

function codeOf(badge: BadgeDto): string {
  return badge.code || badge.id;
}

/**
 * Insignias actualmente "desbloqueadas" a ojos del usuario: la unión de `insignias` (ganadas) e
 * `insigniasProvisionales` (códigos que el récord proyectado alcanza). Para los códigos que solo
 * están en la lista provisional —que viaja como `string[]`, sin objeto `BadgeDto`— se resuelve el
 * badge completo contra `catalogoInsignias`, la misma fuente que usa StreakWidget para pintar la
 * fila. Si el código no aparece en ningún lado (catálogo desactualizado) se descarta: no hay nombre
 * ni umbral que celebrar.
 */
function unlockedBadges(streak: StreakDto): BadgeDto[] {
  const earned = streak.insignias ?? [];
  const provisionalCodes = streak.insigniasProvisionales ?? [];
  const catalog = streak.catalogoInsignias ?? [];

  const byCode = new Map<string, BadgeDto>();
  for (const badge of catalog) byCode.set(codeOf(badge), badge);
  for (const badge of earned) byCode.set(codeOf(badge), badge); // ganada manda sobre el catálogo

  const allCodes = new Set<string>([...earned.map(codeOf), ...provisionalCodes]);
  const resolved: BadgeDto[] = [];
  for (const code of allCodes) {
    const badge = byCode.get(code);
    if (badge) resolved.push(badge);
  }
  return resolved;
}

function readSeenCodes(userId: string): string[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + userId);
    return raw ? (JSON.parse(raw) as string[]) : null;
  } catch (error) {
    if (import.meta.env.DEV) console.error('No se pudieron leer las insignias vistas', error);
    return null;
  }
}

function writeSeenCodes(userId: string, codes: string[]): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + userId, JSON.stringify(codes));
  } catch (error) {
    if (import.meta.env.DEV) console.error('No se pudieron guardar las insignias vistas', error);
  }
}

interface BadgeCelebrationQueue {
  current: BadgeDto | null;
  dismiss: () => void;
}

export function useBadgeCelebrationQueue(streak: StreakDto | undefined): BadgeCelebrationQueue {
  const [queue, setQueue] = useState<BadgeDto[]>([]);

  useEffect(() => {
    if (!streak) return;
    const unlocked = unlockedBadges(streak);
    const seen = readSeenCodes(streak.userId);

    if (seen === null) {
      writeSeenCodes(streak.userId, unlocked.map(codeOf));
      return;
    }

    const seenSet = new Set(seen);
    const newlyUnlocked = unlocked.filter((badge) => !seenSet.has(codeOf(badge)));
    if (newlyUnlocked.length === 0) return;

    writeSeenCodes(streak.userId, [...seen, ...newlyUnlocked.map(codeOf)]);
    setQueue((prev) => [...prev, ...newlyUnlocked]);
  }, [streak]);

  return {
    current: queue[0] ?? null,
    dismiss: () => setQueue((prev) => prev.slice(1)),
  };
}
